/**
 * Dashboard section ids. Fixed order (no user reorder).
 */
export const DASHBOARD_SECTION_IDS = [
  'subscription',
  'rewardsBanner',
  'dailyMacros',
  'selectedMeals',
  'weeklyMacros',
  'onlineOrderImages',
  'communityPosts',
  'addressSetup',
  'reminders',
  'quickActions',
] as const;

export type DashboardSectionId = (typeof DASHBOARD_SECTION_IDS)[number];

/** Fixed dashboard order (top to bottom). */
export const DASHBOARD_FIXED_ORDER: readonly DashboardSectionId[] = DASHBOARD_SECTION_IDS;

export interface DashboardPreferences {
  compact_view: boolean;
  show_calorie_goal: boolean;
}

export const REMINDER_PRESETS = [
  { id: 'water_2', label_key: 'reminders.presetDrinkWater', interval_minutes: 120, icon: 'balance' },
  { id: 'water_3', label_key: 'reminders.presetDrinkWater', interval_minutes: 180, icon: 'balance' },
  { id: 'water_4', label_key: 'reminders.presetDrinkWater', interval_minutes: 240, icon: 'balance' },
  { id: 'stretch_1', label_key: 'reminders.presetStretch', interval_minutes: 60, icon: 'medkit' },
  { id: 'walk_daily', label_key: 'reminders.presetTakeAWalk', interval_minutes: 24 * 60, icon: 'balance' },
] as const;

export type ReminderPresetId = (typeof REMINDER_PRESETS)[number]['id'];

export interface DashboardReminder {
  id: string;
  label: string;
  interval_minutes: number;
  icon: string;
  /** Hex color for icon (e.g. #3B82F6). Omit for theme primary. */
  icon_color?: string | null;
  enabled: boolean;
  last_trigger_at: number | null;
  created_at: number;
}

/** Icons available for custom reminders (must exist in SvgIcon). */
export const REMINDER_ICON_OPTIONS = [
  'bell',
  'balance',
  'medkit',
  'heart',
  'star',
  'time',
  'water',
  'flame',
  'gift',
  'check',
  'calendar',
  'walk',
  'sunny',
  'trophy',
] as const;

/** Color options for reminder icon (hex). */
export const REMINDER_COLOR_OPTIONS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#6366F1', // indigo
] as const;

/** Common interval options for reminders (minutes). Labels resolved via `reminders.everyMinutes` / `everyHours` / `daily`. */
export const REMINDER_INTERVAL_OPTIONS = [
  { value: 15 },
  { value: 30 },
  { value: 60 },
  { value: 120 },
  { value: 180 },
  { value: 240 },
  { value: 360 },
  { value: 720 },
  { value: 24 * 60 },
] as const;

export interface DayMacroSnapshot {
  date: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
}
